import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/components/ui/use-toast';

export interface TherapistTransaction {
  id: string;
  amount: number;
  transaction_type: string;
  status: string;
  description: string;
  commission_amount?: number;
  net_amount?: number;
  created_at: string;
  session_id?: string;
}

export interface TherapistWalletData {
  available_balance: number;
  pending_balance: number;
  total_earned: number;
  currency: string;
}

export const useTherapistWallet = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [wallet, setWallet] = useState<TherapistWalletData | null>(null);
  const [transactions, setTransactions] = useState<TherapistTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  const loadWalletData = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Load wallet
      const { data: walletData, error: walletError } = await supabase
        .from('therapist_wallet')
        .select('*')
        .eq('therapist_id', user.id)
        .single();

      if (walletError) throw walletError;

      setWallet(walletData);

      // Load transactions
      const { data: txData, error: txError } = await supabase
        .from('therapist_transactions')
        .select('*')
        .eq('therapist_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (txError) throw txError;

      setTransactions(txData || []);
    } catch (error: any) {
      console.error('Error loading wallet:', error);
      toast({
        title: "Error",
        description: "Failed to load wallet information",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const requestWithdrawal = async (amount: number, paymentMethod: string, paymentDetails: any) => {
    if (!user) return { success: false, error: 'Not authenticated' };

    if (!wallet || wallet.available_balance < amount) {
      toast({
        title: "Insufficient Balance",
        description: "You don't have enough available balance to withdraw",
        variant: "destructive",
      });
      return { success: false, error: 'Insufficient balance' };
    }

    try {
      const { data, error } = await supabase
        .from('withdrawal_requests')
        .insert({
          therapist_id: user.id,
          amount,
          payment_method: paymentMethod,
          payment_details: paymentDetails
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Withdrawal Requested",
        description: `Your withdrawal request for $${amount} has been submitted`,
      });

      await loadWalletData();

      return { success: true, data };
    } catch (error: any) {
      console.error('Withdrawal error:', error);
      toast({
        title: "Withdrawal Failed",
        description: error.message,
        variant: "destructive",
      });
      return { success: false, error: error.message };
    }
  };

  useEffect(() => {
    loadWalletData();

    // Subscribe to real-time updates
    const channel = supabase
      .channel('therapist-wallet-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'therapist_wallet',
          filter: `therapist_id=eq.${user?.id}`
        },
        () => {
          loadWalletData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  return {
    wallet,
    transactions,
    loading,
    requestWithdrawal,
    refreshWallet: loadWalletData
  };
};
